import Vue from 'vue';
import type { CustomField, CustomizeFieldsSettings } from '../types';
import { useFieldsStorage } from './useFieldsStorage';

// 防抖函数
function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

interface State {
  fields: Record<string, CustomField>;
  loading: boolean;
  error: string | null;
}

// 为每个组件实例创建状态
export function useCustomizeFields(pageType: string, defaultFields: CustomField[] = []) {
  const storage = useFieldsStorage();
  const state = new Vue({
    data: {
      fields: {} as Record<string, CustomField>,
      loading: false,
      error: null as string | null
    }
  }).$data;

  // 结果缓存，避免不必要的重新计算
  let cachedVisibleFields: CustomField[] | null = null;
  let cachedSortedFields: CustomField[] | null = null;

  // 记录上次计算时的字段状态，用于决定是否需要重新计算
  let lastFieldsState = '';

  // 检查是否需要更新缓存
  const shouldUpdateCache = (): boolean => {
    const currentState = JSON.stringify(state.fields);
    if (currentState !== lastFieldsState) {
      lastFieldsState = currentState;
      return true;
    }
    return false;
  };

  // 初始化字段
  const initFields = (initialFields: CustomField[]) => {
    Vue.set(state, 'fields', initialFields.reduce((acc, field) => {
      // 确保所有字段默认可见，除非明确设置为不可见
      acc[field.id] = {
        ...field,
        visible: field.visible !== false // 默认为可见，除非明确设置为 false
      };

      // 特别检查 scheduled 字段
      if (field.id === 'scheduled' && field.visible === undefined) {
        acc[field.id].visible = true;
      }

      return acc;
    }, {} as Record<string, CustomField>));

    // 清除缓存，因为字段已更新
    cachedVisibleFields = null;
    cachedSortedFields = null;
  };

  // 加载保存的设置
  const loadSettings = async () => {
    Vue.set(state, 'loading', true);
    Vue.set(state, 'error', null);
    try {
      const settings = await storage.getSettings(pageType);
      if (settings && validateSettings(settings)) {
        Vue.set(state, 'fields', settings.fields);
      } else {
        initFields(defaultFields);
      }
    } catch (e) {
      Vue.set(state, 'error', e instanceof Error ? e.message : '加载设置失败');
      initFields(defaultFields);
    } finally {
      Vue.set(state, 'loading', false);
    }
  };

  // 验证设置数据
  const validateSettings = (settings: any): settings is CustomizeFieldsSettings => {
    return (
      settings &&
      typeof settings === 'object' &&
      typeof settings.pageType === 'string' &&
      typeof settings.fields === 'object' &&
      Object.values(settings.fields).every(
        (field: any) =>
          typeof field === 'object' &&
          typeof field.id === 'string' &&
          typeof field.label === 'string' &&
          typeof field.visible === 'boolean'
      )
    );
  };

  // 防抖保存设置
  const debouncedSaveSettings = debounce(async () => {
    await saveSettings();
  }, 300);

  // 更新字段可见性
  const updateFieldVisibility = (fieldId: string, visible: boolean) => {
    if (state.fields[fieldId]) {
      // 确保 visible 是一个布尔值
      if (visible === undefined) {
        visible = true;
      }

      // 特别检查 scheduled 字段
      if (fieldId === 'scheduled' && visible === undefined) {
        visible = true;
      }

      Vue.set(state.fields, fieldId, {
        ...state.fields[fieldId],
        visible
      });

      // 清除缓存，因为字段已更新
      cachedVisibleFields = null;
      cachedSortedFields = null;

      void debouncedSaveSettings();
    } else if (fieldId === 'scheduled') {
      // 如果 scheduled 字段不存在，添加它
      Vue.set(state.fields, fieldId, {
        id: fieldId,
        label: 'Scheduled',
        visible: true
      });

      // 清除缓存，因为字段已更新
      cachedVisibleFields = null;
      cachedSortedFields = null;

      void debouncedSaveSettings();
    }
  };

  // 更新字段可见性但不保存到本地存储
  const updateFieldVisibilityWithoutSave = (fieldId: string, visible: boolean) => {
    if (state.fields[fieldId]) {
      // 确保 visible 是一个布尔值
      if (visible === undefined) {
        visible = true;
      }

      // 特别检查 scheduled 字段
      if (fieldId === 'scheduled' && visible === undefined) {
        visible = true;
      }

      Vue.set(state.fields, fieldId, {
        ...state.fields[fieldId],
        visible
      });

      // 清除缓存，因为字段已更新
      cachedVisibleFields = null;
      cachedSortedFields = null;
    } else if (fieldId === 'scheduled') {
      // 如果 scheduled 字段不存在，添加它
      Vue.set(state.fields, fieldId, {
        id: fieldId,
        label: 'Scheduled',
        visible: true
      });

      // 清除缓存，因为字段已更新
      cachedVisibleFields = null;
      cachedSortedFields = null;
    }
  };

  // 重置为默认设置
  const resetToDefault = (saveImmediately = true) => {
    initFields(defaultFields);

    if (saveImmediately) {
      void debouncedSaveSettings();
    }
  };

  // 计算可见字段（使用缓存）
  const getVisibleFields = (): CustomField[] => {
    if (cachedVisibleFields && !shouldUpdateCache()) {
      return cachedVisibleFields;
    }

    cachedVisibleFields = (Object.values(state.fields) as CustomField[]).filter(field => field.visible);
    return cachedVisibleFields;
  };

  // 获取排序后的字段（使用缓存）
  const getSortedFields = (): CustomField[] => {
    if (cachedSortedFields && !shouldUpdateCache()) {
      return cachedSortedFields;
    }

    cachedSortedFields = (Object.values(state.fields) as CustomField[]).sort((a, b) => {
      if (a.required && !b.required) return -1;
      if (!a.required && b.required) return 1;
      return (a.order || 0) - (b.order || 0);
    });

    return cachedSortedFields;
  };

  // 手动保存设置
  const saveSettings = async () => {
    if (state.loading) return;

    Vue.set(state, 'loading', true);
    Vue.set(state, 'error', null);
    try {
      const settings: CustomizeFieldsSettings = {
        userId: 'current-user', // TODO: 从用户上下文获取
        pageType,
        fields: state.fields,
        updatedAt: new Date().toISOString()
      };
      await storage.saveSettings(settings);
    } catch (e) {
      Vue.set(state, 'error', e instanceof Error ? e.message : '保存设置失败');
      throw e;
    } finally {
      Vue.set(state, 'loading', false);
    }
  };

  // 初始加载
  loadSettings();

  return {
    state,
    getVisibleFields,
    getSortedFields,
    updateFieldVisibility,
    updateFieldVisibilityWithoutSave,
    resetToDefault,
    loadSettings,
    saveSettings
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./src/layouts/shipments-4-shippers/components/CustomizeFieldsPanel/composables/useCustomizeFields.ts